import { Location } from '@cobliteam/common-types';
import { type Permission, permissions } from '@cobliteam/permissions';

/**
 * Data-only model for the gamified "Central de Configuração da Frota" (fleet maturity center).
 *
 * No JSX / no icons / no i18n here on purpose: this file is shared by `useBasicValueMaturity`
 * (in the `@cobliteam/hooks` package) and is consumed both by the BasicValue page (apps/dashboard)
 * and by the topbar avatar ring (libs/ui-business). Icons and copy live at the presentation layer,
 * keyed by item id.
 */

/** Minimum number of records a fleet must have for a maturity item to count as done. */
export const MATURITY_MIN_COUNT = 2;

/**
 * Fraction of the fleet's vehicles that must be associated (with a driver, or with a device) for the
 * "driver-association" / "device-association" items to count as done. Requiring 100% — or a fixed count
 * of 2 — is too strict for real/small fleets (a single stray vehicle or a fleet with one device blocks
 * it), so a majority threshold is used: at least half the vehicles need the association.
 */
export const ASSOCIATION_MIN_RATIO = 0.5;

/** Ordered maturity levels (L1 → L5). Names are resolved via i18n at the presentation layer. */
export type MaturityLevelKey = 'connected' | 'organized' | 'monitored' | 'optimized' | 'turbocharged';

/** The three setup areas the items are grouped under. */
export type MaturityAreaKey = 'installation' | 'setup' | 'configuration';

export type MaturityItemId =
  | 'devices'
  | 'vehicles'
  | 'device-association'
  | 'drivers'
  | 'driver-association'
  | 'users'
  | 'groups'
  | 'alerts'
  | 'speed'
  | 'geofences'
  | 'checklists';

export interface MaturityLevelModel {
  key: MaturityLevelKey;
  /** 1-based level number. */
  n: number;
}

export const MATURITY_LEVELS: MaturityLevelModel[] = [
  { key: 'connected', n: 1 },
  { key: 'organized', n: 2 },
  { key: 'monitored', n: 3 },
  { key: 'optimized', n: 4 },
  { key: 'turbocharged', n: 5 },
];

export const MATURITY_AREAS: MaturityAreaKey[] = ['installation', 'setup', 'configuration'];

export interface MaturityItemModel {
  id: MaturityItemId;
  area: MaturityAreaKey;
  level: MaturityLevelKey;
  /** Existing product flow the "Configurar" CTA navigates to. */
  route: Location;
  /** Permission required to act on this item; items the user can't access are excluded from the %. */
  permission: Permission;
}

/**
 * The 11 maturity items, in level-ascending order (the rendering groups them by area).
 *
 * Note: there is no dedicated permission for device/tracker management, so "Instalação" reuses
 * `create-update-delete:crm` (the installation journey's permission); device↔vehicle association
 * and per-vehicle speed limits reuse the vehicle permission.
 */
export const MATURITY_ITEMS: MaturityItemModel[] = [
  // Instalação · Nível 1 (Conectada)
  { id: 'devices', area: 'installation', level: 'connected', route: Location.DEVICES, permission: permissions.CREATE_UPDATE_DELETE_CRM },
  { id: 'vehicles', area: 'installation', level: 'connected', route: Location.VEHICLES, permission: permissions.CREATE_UPDATE_DELETE_VEHICLE },
  { id: 'device-association', area: 'installation', level: 'connected', route: Location.DEVICES, permission: permissions.CREATE_UPDATE_DELETE_VEHICLE },

  // Setup · Nível 2 (Organizada)
  { id: 'drivers', area: 'setup', level: 'organized', route: Location.DRIVER_MANAGEMENT, permission: permissions.CREATE_UPDATE_DELETE_DRIVER },
  { id: 'driver-association', area: 'setup', level: 'organized', route: Location.DRIVER_MANAGEMENT, permission: permissions.CREATE_UPDATE_DELETE_DRIVER },
  { id: 'users', area: 'setup', level: 'organized', route: Location.CONFIG_USERS, permission: permissions.CREATE_UPDATE_DELETE_USERS_AND_INVITES },
  { id: 'groups', area: 'setup', level: 'organized', route: Location.VEHICLES_GROUPS, permission: permissions.CREATE_UPDATE_DELETE_VEHICLE },

  // Configuração da operação · Níveis 3–5
  { id: 'alerts', area: 'configuration', level: 'monitored', route: Location.ALERTS_TRIGGERS_CREATE, permission: permissions.CONFIG_AND_VIEW_CUSTOM_ALERTS },
  { id: 'speed', area: 'configuration', level: 'monitored', route: Location.ROAD_SPEED, permission: permissions.CREATE_UPDATE_DELETE_VEHICLE },
  { id: 'geofences', area: 'configuration', level: 'optimized', route: Location.GEOFENCES_CREATE, permission: permissions.CREATE_UPDATE_DELETE_PLACES_OF_INTEREST },
  { id: 'checklists', area: 'configuration', level: 'turbocharged', route: Location.CHECKLIST_ALL, permission: permissions.FULL_CONTROL_CHECKLIST },
];

/** Status of a single item row: done, the protagonist next step, or a pending (still actionable) step. */
export type MaturityItemStatus = 'done' | 'next' | 'todo';

/** Status of a level node in the maturity track. */
export type MaturityNodeStatus = 'done' | 'current' | 'locked';
